import { Dispatch, ReactNode, SetStateAction } from 'react';
import { styled } from 'styled-components';

import { Assets } from '../../assets/index';
import { Flex } from '../Layout';
import { Text } from '../Text';
import { Colors } from '../../utils';
import { CircleInfoIcon, TriangleExclamationIcon } from '../../assets/icons';
import { StyledTriangle } from '../Triangle';
import { Button } from '../Button';

type TableFeedbackProps = {
  /** Background color and icon*/
  type: 'error' | 'warning' | 'information';
  /** Used to open a modal from the component, pass modal component as prop */
  modalComponent?: ReactNode;
  children: ReactNode;
  marginLeft?: string;
  setClosableFeedbackState: Dispatch<
    SetStateAction<{ feedbackWarning?: boolean; feedbackInformation?: boolean; closedInformation?: boolean }>
  >;
  /** Reset button will only be visible if this is set. */
  onResetButtonClick?: () => void;
};

export const TableFeedback = ({
  type,
  modalComponent,
  marginLeft,
  children,
  setClosableFeedbackState,
  onResetButtonClick,
}: TableFeedbackProps) => {
  const distanceToInput = -5;

  return (
    <StyledFlex
      backgroundColor={colorMap[type]}
      paddingTop={'6px'}
      paddingBottom={'6px'}
      paddingRight={16}
      paddingLeft={12}
      position="absolute"
      maxHeight={58}
      minWidth="fit-content"
      marginLeft={marginLeft}
      style={{
        width: 'max-content',
        right: distanceToInput,
        fontWeight: 400,
        fontSize: 14,
        minHeight: '40px',
      }}
    >
      <Flex alignItems="center" columnGap={9}>
        <Flex maxWidth={300} marginRight={6} alignItems="center" columnGap="10px" style={{ textAlign: 'left' }}>
          <Icon type={type} />
          {typeof children === 'string' ? <Text size="small">{children}</Text> : children}
        </Flex>
        {modalComponent}

        {type === 'information' && (
          <Button
            styleAs="link"
            style={{ maxWidth: 'fit-content' }}
            fontSize="small"
            onClick={() => setClosableFeedbackState({ feedbackInformation: false, closedInformation: true })}
          >
            <Assets.MenuCloseIcon color="foraBlue" width={14} marginRight={5} />
            Stäng
          </Button>
        )}
        {type === 'warning' && (
          <Button
            styleAs="link"
            style={{ maxWidth: 'fit-content' }}
            fontSize="small"
            onClick={() => setClosableFeedbackState({ feedbackWarning: false })}
          >
            <Assets.MenuCloseIcon color="foraBlue" width={14} marginRight={5} />
            Stäng
          </Button>
        )}

        {onResetButtonClick && (
          <Button styleAs="link" style={{ maxWidth: 'fit-content' }} fontSize="small" onClick={onResetButtonClick}>
            <Assets.ResetIcon color="foraBlue" width={14} marginRight={5} />
            Återställ
          </Button>
        )}
      </Flex>
      <StyledTriangle color={colorMap[type]} style={{ rotate: '90deg', top: '40%', right: '-14px' }} />
    </StyledFlex>
  );
};

const colorMap: { [key in TableFeedbackProps['type']]: Colors } = {
  error: 'babyPink',
  warning: 'babyYellow',
  information: 'babyBlue',
};
const Icon = ({ type }: { type: TableFeedbackProps['type'] }) => {
  switch (type) {
    case 'error':
      return <CircleInfoIcon width={22} />;
    case 'warning':
      return <TriangleExclamationIcon width={22} />;
    case 'information':
      return <CircleInfoIcon width={22} />;
  }
};

const StyledFlex = styled(Flex)`
  animation: fade-in 0.2s;

  @keyframes fade-in {
    from {
      opacity: 0;
    }
    to {
      opacity: 100;
    }
  }
`;
